import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { testIsActiveLink } from '~/eds/src/modules/PageHeader/utils';
import { NavLink } from '~/eds/src/types/NavLink';

import { SidebarNavControl } from './SidebarNavControl';
import './SidebarNavFlyout.css';

/** Gap between the collapsed rail and the flyout. */
const OFFSET_PX = 4;
/** Keeps the flyout off the very edge of the viewport when it has to shift up. */
const VIEWPORT_PADDING_PX = 8;
/**
 * Grace period before a pointer leave closes the flyout, so the diagonal travel
 * from the rail across the gap and into the panel doesn't dismiss it.
 */
const CLOSE_DELAY_MS = 150;

interface SidebarNavFlyoutProps {
  /** The parent section's label, shown as the flyout's overline header. */
  label: string;
  links: NavLink[];
  activePathname: string;
  onActivate: (path: string) => void;
  /** The collapsed rail item the flyout is anchored to. */
  children: ReactNode;
}

/**
 * Nested nav items for the collapsed rail, where there is no room to expand a
 * section inline. Opens on hover and on keyboard focus, and is built on the
 * native Popover API — so it renders in the top layer (never clipped by the
 * nav's scroll container) and gets light dismiss and Escape for free.
 *
 * Positioning is done in JS rather than with CSS anchor positioning, which
 * Firefox does not support yet.
 */
export function SidebarNavFlyout({
  label,
  links,
  activePathname,
  onActivate,
  children,
}: SidebarNavFlyoutProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isOpen, setIsOpen] = useState(false);
  const headerId = useId();

  const cancelScheduledClose = () => {
    clearTimeout(closeTimeoutRef.current);
  };

  const open = () => {
    cancelScheduledClose();
    setIsOpen(true);
  };

  const close = () => {
    cancelScheduledClose();
    setIsOpen(false);
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(
      () => setIsOpen(false),
      CLOSE_DELAY_MS,
    );
  };

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  const position = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const { height } = popover.getBoundingClientRect();
    const maxTop = window.innerHeight - height - VIEWPORT_PADDING_PX;
    const top = Math.max(
      VIEWPORT_PADDING_PX,
      Math.min(anchorRect.top, Math.max(maxTop, VIEWPORT_PADDING_PX)),
    );

    // Popovers are in the top layer and fixed-positioned, so viewport
    // coordinates apply directly.
    popover.style.top = `${top}px`;
    popover.style.insetInlineStart = `${anchorRect.right + OFFSET_PX}px`;
    popover.style.maxHeight = `${window.innerHeight - 2 * VIEWPORT_PADDING_PX}px`;
  }, []);

  useEffect(() => {
    const popover = popoverRef.current;
    if (!isOpen || !popover) return;

    position();
    // Guarded for jsdom and any browser without the Popover API — the panel
    // still renders and is usable, just without the top layer.
    if (typeof popover.showPopover === 'function') {
      popover.showPopover();
    }

    // Escape and outside clicks are handled by the browser, which closes the
    // popover without telling React — sync our state back off the toggle event.
    const handleToggle = (event: Event) => {
      if ((event as ToggleEvent).newState === 'closed') {
        setIsOpen(false);
      }
    };

    // The rail is sticky but the nav list scrolls, so the anchor can move
    // underneath an open flyout.
    popover.addEventListener('toggle', handleToggle);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      popover.removeEventListener('toggle', handleToggle);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [isOpen, position]);

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (nextTarget && popoverRef.current?.contains(nextTarget)) return;
    close();
  };

  return (
    <div
      className={`sidebar-flyout-anchor${isOpen ? ' sidebar-flyout-anchor--open' : ''}`}
      ref={anchorRef}
      onBlur={handleBlur}
      onFocus={open}
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
    >
      {children}
      {isOpen && (
        // The mouse handlers are hover-intent bookkeeping, not an interaction:
        // they only keep the panel from closing while the pointer is inside it.
        // Everything actionable in here is a link, and the panel is reachable
        // and dismissable by keyboard without them.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          aria-labelledby={headerId}
          className="sidebar-flyout"
          popover="auto"
          ref={popoverRef}
          role="group"
          onBlur={handleBlur}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        >
          <span className="sidebar-flyout-header" id={headerId}>
            {label}
          </span>
          {links.map((link) => (
            <SidebarNavControl
              className="sidebar-flyout-item"
              isActive={testIsActiveLink(activePathname)(link)}
              key={link.pathname}
              pathname={link.pathname}
              onActivate={(pathname: string) => {
                close();
                onActivate(pathname);
              }}
            >
              <span className="sidebar-flyout-item-label">{link.label}</span>
            </SidebarNavControl>
          ))}
        </div>
      )}
    </div>
  );
}
